import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { Button, Loader, Progress, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconCamera, IconPhotoUp, IconTrash } from '@tabler/icons-react';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { isTouchDevice, useAccountPhotoActions, useMyPhoto, type PhotoStage } from '@/lib/photo';
import { photoRules, useCopy } from '@/lib/settings';
import { Callout } from '@/components/ui';
import { Disclosure } from '@/components/member/Disclosure';
import { PersonAvatar } from './PersonAvatar';
import '@/styles/member.css';

const STAGE_TEXT: Record<PhotoStage, string> = {
  idle: '',
  preparing: 'Getting your photo ready…',
  uploading: 'Uploading…',
  saving: 'Saving…',
  removing: 'Removing your photo…',
};

interface Props {
  /** Anchor id (Account uses "photo" so Home's nudge can link to /account#photo). */
  id?: string;
  /** Shown above the card body, e.g. the driver nudge or the "photo required" warning. */
  notice?: ReactNode;
  /** Extra line in the remove confirmation (e.g. "you need a photo to offer lifts"). */
  removeWarning?: string;
  /** Called after a successful upload or removal. */
  onSaved?: (hasPhoto: boolean) => void;
  /** No card of its own (the page already puts it inside a SectionCard). */
  plain?: boolean;
}

/**
 * The member's optional profile photo (their Acts Church account photo): large preview, Choose a
 * photo / Take a photo (phones) / Remove with a confirm, progress and inline errors. Renders
 * nothing when photos are switched off in settings.
 */
export function ProfilePhotoCard({ id, notice, removeWarning, onSaved, plain }: Props) {
  const { t, settings } = useCopy();
  const rules = photoRules(settings);
  const { getUserDisplayName } = useEnrichedAuth();
  const mine = useMyPhoto();
  const actions = useAccountPhotoActions();
  const galleryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const touch = useMemo(isTouchDevice, []);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!status) return;
    const timer = window.setTimeout(() => setStatus(null), 5000);
    return () => window.clearTimeout(timer);
  }, [status]);

  if (!rules.enabled) return null;

  const name = getUserDisplayName();
  const src = actions.previewUrl ?? mine.url;
  const hasPhoto = !!src;
  const { busy } = actions;

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = ''; // allow picking the same file again
    if (!file) return;
    setStatus(null);
    const ok = await actions.upload(file);
    if (ok) {
      setStatus('Photo saved.');
      onSaved?.(true);
    }
  };

  const confirmRemove = () =>
    modals.openConfirmModal({
      title: 'Remove your photo?',
      centered: true,
      children: (
        <Text size="md">
          This removes your Acts Church profile photo in every church app, not just Carpool.
          {removeWarning ? ` ${removeWarning}` : ''}
        </Text>
      ),
      labels: { confirm: 'Remove photo', cancel: 'Keep it' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setStatus(null);
        const ok = await actions.remove();
        if (ok) {
          setStatus('Photo removed.');
          onSaved?.(false);
        }
      },
    });

  return (
    <div className={plain ? undefined : 'rides-card'} id={id} style={{ padding: plain ? 0 : 16, scrollMarginTop: 80 }}>
      {notice && <div style={{ marginBottom: 12 }}>{notice}</div>}
      <div className="rm-photo">
        <div className="pic">
          <PersonAvatar src={src} name={name} size="xl" alt="Your profile photo" />
          {busy && (
            <span className="rides-photo-busy" aria-hidden="true">
              <Loader size="sm" color="white" />
            </span>
          )}
        </div>
        <div className="txt">
          <b>{hasPhoto ? 'This is your photo' : 'No photo yet'}</b>
          <p>{t('photo.intro')}</p>
        </div>
      </div>

      {busy && (
        <div style={{ marginTop: 12 }} role="status" aria-live="polite">
          <Progress value={actions.stage === 'removing' ? 100 : actions.progress} animated striped aria-label={STAGE_TEXT[actions.stage]} size="sm" />
          <p className="rm-small" style={{ marginTop: 4 }}>{STAGE_TEXT[actions.stage]}</p>
        </div>
      )}

      <div className="rm-btnrow" style={{ marginTop: 14 }}>
        <Button
          leftSection={<IconPhotoUp size={18} />}
          onClick={() => galleryRef.current?.click()}
          disabled={busy}
          variant={hasPhoto ? 'default' : 'filled'}
        >
          {hasPhoto ? 'Change photo' : 'Choose a photo'}
        </Button>
        {touch && (
          <Button leftSection={<IconCamera size={18} />} onClick={() => cameraRef.current?.click()} disabled={busy} variant="default">
            Take a photo
          </Button>
        )}
      </div>
      {hasPhoto && (
        <Button leftSection={<IconTrash size={18} />} onClick={confirmRemove} disabled={busy} variant="subtle" color="red" fullWidth mt={8}>
          Remove my photo
        </Button>
      )}
      <input ref={galleryRef} type="file" accept="image/*" onChange={onPick} style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />
      <input ref={cameraRef} type="file" accept="image/*" capture="user" onChange={onPick} style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />

      {actions.error && (
        <div style={{ marginTop: 12 }}>
          <Callout tone="bad" live action={<Button variant="default" size="sm" onClick={actions.clearError}>OK</Button>}>{actions.error}</Callout>
        </div>
      )}
      <div role="status" aria-live="polite">
        {status && !actions.error && <p className="rm-ok-text" style={{ marginTop: 10 }}>{status}</p>}
      </div>

      <div style={{ marginTop: 6 }}>
        <Disclosure label="Who sees my photo?">
          <p className="rm-small" style={{ color: 'var(--rides-ink)', fontSize: 15 }}>{t('photo.privacy_note')}</p>
          <p className="rm-small" style={{ marginTop: 6 }}>{t('photo_card.resize_note')}</p>
        </Disclosure>
      </div>
    </div>
  );
}
